import { Schema } from "effect";
import {
  ApiEnvironmentIdSchema,
  ApiKeyIdSchema,
  ApiProjectIdSchema,
  EnvironmentIdSchema,
  IdSchema,
  ProjectIdSchema,
  RequestIdSchema,
  UserIdSchema,
  WorkspaceIdSchema,
} from "./ids";
import {
  PapersFlowBoundaryParseOptions,
  IsoDateTimeSchema,
  NonNegativeSafeIntegerSchema,
} from "./standard";

export const MoneyMicrosSchema = NonNegativeSafeIntegerSchema.pipe(Schema.brand("MoneyMicros"));
export const SubscriptionStatusSchema = Schema.Literals([
  "trialing",
  "active",
  "past_due",
  "paused",
  "canceled",
  "incomplete",
]);

export const EntitlementsSchema = Schema.Struct({
  agentAccess: Schema.Boolean,
  apiAccess: Schema.Boolean,
  connectorAccess: Schema.Boolean,
  browserAgent: Schema.Boolean,
  mcpAccess: Schema.Boolean,
  maxConcurrentAgents: NonNegativeSafeIntegerSchema,
  monthlyCreditsMicros: MoneyMicrosSchema,
  requestsPerMinute: NonNegativeSafeIntegerSchema,
  currentPeriodEndsAt: Schema.optionalKey(IsoDateTimeSchema),
  sourceUpdatedAt: IsoDateTimeSchema,
});

export const UsageEventSchema = Schema.Struct({
  eventId: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(160)),
  requestId: RequestIdSchema,
  workspaceId: WorkspaceIdSchema,
  projectId: Schema.optionalKey(ProjectIdSchema),
  environmentId: Schema.optionalKey(EnvironmentIdSchema),
  apiProjectId: Schema.optionalKey(ApiProjectIdSchema),
  apiEnvironmentId: Schema.optionalKey(ApiEnvironmentIdSchema),
  apiKeyId: Schema.optionalKey(ApiKeyIdSchema),
  runId: Schema.optionalKey(IdSchema),
  userId: Schema.optionalKey(UserIdSchema),
  provider: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(80)),
  model: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(160)),
  operation: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(120)),
  inputTokens: NonNegativeSafeIntegerSchema,
  outputTokens: NonNegativeSafeIntegerSchema,
  cachedInputTokens: NonNegativeSafeIntegerSchema,
  reasoningTokens: NonNegativeSafeIntegerSchema,
  providerCostMicros: MoneyMicrosSchema,
  infrastructureCostMicros: MoneyMicrosSchema,
  billableCostMicros: MoneyMicrosSchema,
  currency: Schema.String.check(
    Schema.isMinLength(3),
    Schema.isMaxLength(3),
    Schema.isUppercased(),
  ),
  priceBookVersion: Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(80)),
  occurredAt: IsoDateTimeSchema,
}).check(
  Schema.makeFilter(
    (event) =>
      event.environmentId === undefined ||
      event.projectId !== undefined ||
      "runtime environment usage requires its workspace Project",
  ),
  Schema.makeFilter(
    (event) =>
      event.apiEnvironmentId === undefined ||
      event.apiProjectId !== undefined ||
      "API environment usage requires its API project",
  ),
  Schema.makeFilter(
    (event) =>
      event.apiKeyId === undefined ||
      (event.apiProjectId !== undefined && event.apiEnvironmentId !== undefined) ||
      "API key usage requires its API project and environment",
  ),
  Schema.makeFilter(
    (event) =>
      event.runId === undefined ||
      event.projectId !== undefined ||
      "agent run usage requires its workspace Project",
  ),
);

export const SubscriptionProjectionSchema = Schema.Struct({
  workspaceId: WorkspaceIdSchema,
  polarCustomerId: Schema.optionalKey(Schema.Trim.check(Schema.isMaxLength(256))),
  polarSubscriptionId: Schema.optionalKey(Schema.Trim.check(Schema.isMaxLength(256))),
  productKey: Schema.optionalKey(Schema.Trim.check(Schema.isMaxLength(120))),
  status: SubscriptionStatusSchema,
  seatQuantity: NonNegativeSafeIntegerSchema,
  currentPeriodStartsAt: Schema.optionalKey(IsoDateTimeSchema),
  currentPeriodEndsAt: Schema.optionalKey(IsoDateTimeSchema),
  cancelAtPeriodEnd: Schema.Boolean,
  lastEventId: Schema.Trim.check(Schema.isMaxLength(256)),
  updatedAt: IsoDateTimeSchema,
});

export const decodeEntitlements = Schema.decodeUnknownSync(
  EntitlementsSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeMoneyMicros = Schema.decodeSync(
  MoneyMicrosSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeUsageEvent = Schema.decodeUnknownSync(
  UsageEventSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeSubscriptionProjection = Schema.decodeUnknownSync(
  SubscriptionProjectionSchema,
  PapersFlowBoundaryParseOptions,
);

export type MoneyMicros = typeof MoneyMicrosSchema.Type;
export type MoneyMicrosEncoded = typeof MoneyMicrosSchema.Encoded;
export type Entitlements = typeof EntitlementsSchema.Type;
export type EntitlementsEncoded = typeof EntitlementsSchema.Encoded;
export type UsageEvent = typeof UsageEventSchema.Type;
export type UsageEventEncoded = typeof UsageEventSchema.Encoded;
export type SubscriptionProjection = typeof SubscriptionProjectionSchema.Type;
export type SubscriptionProjectionEncoded = typeof SubscriptionProjectionSchema.Encoded;
